"use client";
import {
  Bell,
  BookOpen,
  ChefHat,
  Home,
  Lock,
  LogIn,
  Map,
  Menu,
  Search,
  Store,
  User,
  X
} from "lucide-react";
import { signOut, useSession } from "next-auth/react";
import { useTranslations } from "next-intl";
import { usePathname, useRouter } from "next/navigation";
import { useEffect, useState } from "react";

import { RestaurantLeadForm } from "@/components/leads/RestaurantLeadForm";
import { Logo } from "@/components/logo";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from "@/components/ui/sheet";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger
} from "@/components/ui/tooltip";
import { Locale } from "@/i18n";
import { cn } from "@/lib/utils";
import { Link } from "@nextui-org/react";
import { QuestionMarkIcon } from "@radix-ui/react-icons";
import { LanguageSwitcher } from "./language-switcher";
import { ThemeSwitcher } from "./theme-switcher";

const icons = {
  home: Home,
  search: Search,
  restaurants: ChefHat,
  map: Map,
  events: Bell,
  about: BookOpen,
  faq: QuestionMarkIcon,
  privacy: Lock
} as const;

type IconType = keyof typeof icons;

interface NavigationItem {
  label: string;
  href: string;
  icon: IconType;
}

const NavigationLinks: NavigationItem[] = [
  { label: "home", href: "/", icon: "home" },
  { label: "search", href: "/search", icon: "search" },
  { label: "faq", href: "/faq", icon: "faq" }
];

interface NavbarProps {
  locale?: Locale
}

export function Navbar({ }: NavbarProps) {
  const { data: session } = useSession();
  const t = useTranslations("navigation");
  const leadsT = useTranslations("leads");
  const pathname = usePathname();
  const router = useRouter();
  const [scrolled, setScrolled] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const [leadFormOpen, setLeadFormOpen] = useState(false);
  const [navVisible, setNavVisible] = useState(false);

  const isSignedIn = !!session;

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 20);
    };

    window.addEventListener("scroll", handleScroll, { passive: true });

    // Start animation after component mounts
    const timer = setTimeout(() => setNavVisible(true), 100);

    return () => {
      window.removeEventListener("scroll", handleScroll);
      clearTimeout(timer);
    };
  }, []);

  const handleSignOut = async () => {
    await signOut({ redirect: false });
    router.push(`/${pathname.split('/')[1] || ''}`);
    router.refresh();
  };

  const NavLink = ({ href, icon, label }: NavigationItem) => {
    const Icon = icons[icon];
    const isActive = pathname === href;

    return (
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger asChild>
            <Link
              href={href}
              className={cn(
                "group flex items-center gap-2.5 px-3 py-2 rounded-xl text-sm font-medium",
                "transition-all duration-300 ease-out",
                "hover:bg-emerald-100/70 hover:text-emerald-700 dark:hover:bg-emerald-800/50 dark:hover:text-emerald-100",
                isActive ? "bg-emerald-200/70 text-emerald-800 dark:bg-emerald-700/60 dark:text-emerald-50" : "text-emerald-700 dark:text-emerald-200",
                "relative",
                "md:px-4 md:py-2.5",
                "hover:scale-105"
              )}
              onClick={() => setIsOpen(false)}
            >
              {Icon && (
                <Icon className={cn(
                  "w-4 h-4 flex-shrink-0 transition-transform duration-300",
                  "group-hover:scale-110",
                  isActive && "text-emerald-600 dark:text-emerald-400"
                )} />
              )}
              <span className="font-medium">{t(label)}</span>
              {isActive && (
                <span className="absolute bottom-0 left-0 h-0.5 w-full bg-emerald-500/70 rounded-full 
                transform origin-left transition-transform duration-300" />
              )}
            </Link>
          </TooltipTrigger>
          <TooltipContent>
            <p>{t(`${label}`)}</p>
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
    );
  };

  const UserMenu = () => {
    const initial = session?.user?.name?.[0] || session?.user?.email?.[0] || "U";

    return (
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant="ghost" className="relative h-9 w-9 rounded-xl overflow-hidden p-0">
            <Avatar className="h-9 w-9 transition-transform duration-300 hover:scale-110">
              <AvatarImage src={session?.user?.image || ""} alt={session?.user?.name || "User"} />
              <AvatarFallback className="bg-emerald-100 text-emerald-700 dark:bg-emerald-800 dark:text-emerald-100">{initial.toUpperCase()}</AvatarFallback>
            </Avatar>
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent className="w-56" align="end" forceMount>
          <div className="flex flex-col space-y-1 p-2">
            <p className="text-sm font-medium leading-none">{session?.user?.name || "User"}</p>
            <p className="text-xs leading-none text-muted-foreground">{session?.user?.email}</p>
          </div>
          <DropdownMenuSeparator />
          <DropdownMenuItem onClick={() => router.push("/profile")} className="cursor-pointer">
            <User className="mr-2 h-4 w-4" />
            <span>Profile</span>
          </DropdownMenuItem>
          <DropdownMenuSeparator />
          <DropdownMenuItem onClick={handleSignOut} className="cursor-pointer">
            <LogIn className="mr-2 h-4 w-4 rotate-180" />
            <span>{t('signout')}</span>
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    );
  };

  const SettingsGroup = () => {
    return (
      <div className="flex items-center gap-2 p-2 rounded-xl bg-emerald-100/50 dark:bg-emerald-800/40 backdrop-blur-sm">
        <LanguageSwitcher />
        <div className="w-px h-4 bg-emerald-400/30 dark:bg-emerald-600/30" />
        <ThemeSwitcher />
      </div>
    );
  };

  return (
    <header className={cn(
      "w-full sticky top-0 z-50 transition-all duration-300",
      scrolled && "bg-white/80 dark:bg-slate-950/80 shadow-sm backdrop-blur-md",
      !scrolled && "bg-transparent"
    )}>
      <div className="container mx-auto px-4">
        <div className="flex h-16 items-center justify-between">
          {/* Logo and Desktop Navigation */}
          <div className="flex items-center gap-8">
            <div className={cn(
              "transition-all duration-700 ease-out",
              navVisible ? "opacity-100 translate-y-0" : "opacity-0 -translate-y-4"
            )}>
              <Logo />
            </div>

            <div className="h-5 w-px bg-emerald-400/30 dark:bg-emerald-600/30 hidden md:block" />

            {/* Desktop Navigation */}
            <nav className="hidden md:flex items-center gap-1">
              {NavigationLinks.map((link, index) => (
                <div
                  key={link.href}
                  className={cn(
                    "transition-all duration-700 ease-out",
                    navVisible ? "opacity-100 translate-y-0" : "opacity-0 -translate-y-4",
                    // Stagger the animation
                    navVisible && `transition-delay-${(index + 1) * 100}`
                  )}
                >
                  <NavLink {...link} />
                </div>
              ))}
            </nav>
          </div>

          {/* Right Side Actions */}
          <div className={cn(
            "flex items-center gap-3",
            "transition-all duration-700 ease-out",
            navVisible ? "opacity-100 translate-y-0" : "opacity-0 -translate-y-4",
            navVisible && "delay-300"
          )}>
            {/* Restaurant Lead Button */}
            <Button
              variant="outline"
              size="sm"
              onClick={() => setLeadFormOpen(true)}
              className="hidden md:flex items-center gap-2 rounded-xl border-emerald-500 text-emerald-600 hover:bg-emerald-50 hover:scale-105 transition-all duration-300 dark:border-emerald-400 dark:text-emerald-300 dark:hover:bg-emerald-900/30"
            >
              <Store className="h-4 w-4" />
              <span>{leadsT('forRestaurants')}</span>
            </Button>

            {/* Restaurant Lead Form */}
            <RestaurantLeadForm
              open={leadFormOpen}
              onOpenChange={setLeadFormOpen}
            />

            {/* Settings Group - ONLY visible on desktop */}
            <div className="hidden md:block">
              <SettingsGroup />
            </div>

            {/* Auth Section */}
            <div className="flex-shrink-0">
              {!isSignedIn ? (
                <Button
                  variant="default"
                  size="sm"
                  className="rounded-xl gap-2 bg-emerald-700 text-white hover:bg-emerald-800 hover:scale-105 transition-all duration-300"
                  onClick={() => router.push("/login")}
                >
                  <LogIn className="w-4 h-4" />
                  {t('signin')}
                </Button>
              ) : (
                <UserMenu />
              )}
            </div>

            {/* Mobile Menu Button */}
            <Sheet open={isOpen} onOpenChange={setIsOpen}>
              <SheetTrigger asChild>
                <Button
                  variant="ghost"
                  size="icon"
                  className="md:hidden relative w-10 h-10 rounded-xl hover:bg-emerald-100/70 dark:hover:bg-emerald-800/50 hover:scale-105 transition-all duration-300 text-emerald-700 dark:text-emerald-200"
                >
                  {isOpen ? (
                    <X className="h-5 w-5 transition-all duration-300 rotate-90" />
                  ) : (
                    <Menu className="h-5 w-5 transition-all duration-300" />
                  )}
                  <span className="sr-only">Toggle menu</span>
                </Button>
              </SheetTrigger>

              {/* Mobile Menu Content */}
              <SheetContent side="right" className="w-full max-w-[280px] p-0 bg-white dark:bg-slate-950">
                <SheetHeader className="px-4 py-4 border-b border-emerald-300/70 dark:border-emerald-700/50">
                  <SheetTitle className="text-left text-emerald-700 dark:text-emerald-100">Navigation</SheetTitle>
                </SheetHeader>

                <div className="flex flex-col h-[calc(100vh-4rem)]">
                  {/* Mobile Navigation */}
                  <div className="flex-1 overflow-y-auto px-3 py-4">
                    <div className="flex flex-col gap-2">
                      {NavigationLinks.map((link) => (
                        <NavLink key={link.href} {...link} />
                      ))}

                      {/* Restaurant Lead Mobile Button */}
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => {
                          setLeadFormOpen(true);
                          setIsOpen(false);
                        }}
                        className="flex items-center justify-start gap-2 rounded-xl border-emerald-500 text-emerald-600 hover:bg-emerald-50 hover:scale-105 transition-all duration-300 dark:border-emerald-400 dark:text-emerald-300 dark:hover:bg-emerald-900/30 mt-4"
                      >
                        <Store className="h-4 w-4" />
                        <span>{leadsT('forRestaurants')}</span>
                      </Button>

                      {/* Theme and Language Settings for Mobile */}
                      <div className="mt-6 mb-2">
                        <h3 className="text-xs font-medium text-emerald-700 dark:text-emerald-300 mb-2">Settings</h3>
                        <div className="flex flex-col gap-3">
                          <div className="flex items-center justify-between">
                            <span className="text-sm text-emerald-800 dark:text-emerald-200">Language</span>
                            <LanguageSwitcher />
                          </div>
                          <div className="flex items-center justify-between">
                            <span className="text-sm text-emerald-800 dark:text-emerald-200">Theme</span>
                            <ThemeSwitcher />
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Mobile Settings & Auth */}
                  <div className="p-4 border-t border-emerald-300/70 dark:border-emerald-700/50 space-y-4">
                    {/* Mobile Sign In/Out */}
                    {!isSignedIn ? (
                      <Button
                        size="default"
                        variant="default"
                        className="w-full gap-2 rounded-xl bg-emerald-700 text-white hover:bg-emerald-800 hover:scale-105 transition-all duration-300"
                        onClick={() => {
                          router.push("/login");
                          setIsOpen(false);
                        }}
                      >
                        <LogIn className="w-4 h-4" />
                        {t('signin')}
                      </Button>
                    ) : (
                      <Button
                        size="default"
                        variant="outline"
                        className="w-full gap-2 rounded-xl border-emerald-500 hover:bg-emerald-50 hover:scale-105 transition-all duration-300 dark:border-emerald-400 dark:hover:bg-emerald-900/30"
                        onClick={() => {
                          handleSignOut();
                          setIsOpen(false);
                        }}
                      >
                        <LogIn className="w-4 h-4 rotate-180" />
                        {t('signout')}
                      </Button>
                    )}
                  </div>
                </div>
              </SheetContent>
            </Sheet>
          </div>
        </div>
      </div>
    </header>
  );
}

export default Navbar;